const defaultSourceString = 'N/A';

/**
 * Given a string, generate an HSL code
 * https://medium.com/@pppped/compute-an-arbitrary-color-for-user-avatar-starting-from-his-username-with-javascript-cd0675943b66
 * @param  {string} sourceString
 * @param  {number|string} saturation
 * @param  {number|string} lightness
 * @return {string}
 */
export const stringToHsl = (
    sourceString: string = defaultSourceString,
    saturation: number | string = 50,
    lightness: number | string = 70,
): string => {
    let hash = 0;
    let i;

    for (i = 0; i < sourceString.length; i += 1) {
        hash = sourceString.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = hash % 360;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Return true when a hex colour is "light" (relative luminance > 0.5) and so
 * needs dark text on top. Accepts colours with or without a leading `#`, and
 * both three- and six-character hex codes.
 */
export const needsDarkText = (hexColor: string): boolean => {
    if (!hexColor) {
        return false;
    }

    // Remove # if present
    let color = hexColor.replace('#', '');

    // Expand a three-character hexcode to six characters
    if (color.length === 3) {
        color = color.split('').map((hex) => hex + hex).join('');
    }

    // Convert hex to RGB
    const r = parseInt(color.substring(0, 2), 16);
    const g = parseInt(color.substring(2, 4), 16);
    const b = parseInt(color.substring(4, 6), 16);

    // Calculate relative luminance using the WCAG formula
    const luminance = ((0.299 * r) + (0.587 * g) + (0.114 * b)) / 255;

    // Return true for light colors (luminance > 0.5)
    return luminance > 0.5;
};

/**
 * Given a hex string, return whether the contrast text colour should be dark
 * ('black' on a light background) or light ('white' on a dark background).
 */
export const getContrastTextColor = (color: string): string => (needsDarkText(color) ? 'black' : 'white');